'use client';

import React, { useState, useEffect, useRef } from 'react';
import type { MouseEvent } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { NotificationBanner, type NotificationBannerProps, type NotificationBannerCarouselItem } from '@enter/business-components/components/notification-banner';
import { cn } from '@workspace/ui/lib/utils';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Logo } from '@/components/logo';
import { useEnterHomeLogin } from '@/contexts/auth/use-enter-home-login';
import {
    createEnterHomeAuthAttempt,
    type EnterHomeAuthAttempt,
} from '@/contexts/auth/login-analytics';
import { useMixpanel } from '@/contexts/mixpanel';
import { useLocalizedNavigation } from '@/contexts/i18n/navigation';
import { stripLocalePrefix } from '@/contexts/i18n/utils';
import {
    ENTER_HOME_PRICING_PATH,
    buildEnterHomePricingNavigationTrack,
    buildEnterHomePricingUrl,
    openEnterHomePricingWindow,
} from '@/lib/enter-home-pricing-route';
import { CONTACT_SALE_PATH } from '@/lib/contact-sale-route';
import { appendTrackingParams } from '@/lib/utm';
import { getHeaderBannerLayout } from '@/components/site-header/banner-layout';
import '@/components/site-header/index.css';

type LandingV2ActionType = 'login' | 'navigate' | 'open_modal' | 'scroll' | 'select' | 'submit' | 'toggle';
type LandingButtonTrackingAttributes = Record<`data-landing-${string}`, string>;

interface SubMenuItem {
    labelKey: string;
    href: string;
    internal?: boolean;  // Changed: internal flag instead of external
}

interface NavItem {
    labelKey: string;
    href?: string;
    internal?: boolean;  // Changed: internal flag instead of external
    environmentRoute?: 'pricing';
    showExternalIcon?: boolean;
    dropdown?: boolean;
    subMenus?: {
        [key: string]: SubMenuItem[];
    };
}

const ENTER_FEATURES_PATH = '/features';
/** Solutions articles live in their own URL space on the blog origin. */
const ENTER_SOLUTIONS_URL = 'https://enter.converge.ai/solutions';
const SIGNIN_POPUP_DISMISSED_KEY = 'enter-home:signin-popup-dismissed';

const NAV_ITEMS: NavItem[] = [
    // Home - external link to main site
    { labelKey: 'nav.home', href: 'https://enter.converge.ai/', internal: false },
    
    // Product - all internal links
    { 
        labelKey: 'nav.enterCreative', 
        dropdown: true,
        subMenus: {
            'product': [
                { labelKey: 'dropdown.aiAll', href: '/ai-all', internal: true },
                { labelKey: 'dropdown.enterCode', href: '/code', internal: true },
                { labelKey: 'dropdown.enterCli', href: '/cli', internal: true },
                { labelKey: 'dropdown.templates', href: '/marketplace/templates', internal: true },
                { labelKey: 'dropdown.components', href: '/marketplace/components', internal: true },
            ],
            'features': [
                { labelKey: 'dropdown.websiteTemplates', href: `${ENTER_FEATURES_PATH}/website-template`, internal: false },
                { labelKey: 'dropdown.aiAgentBuilder', href: `${ENTER_FEATURES_PATH}/ai-agent-builder`, internal: false },
                { labelKey: 'dropdown.visualEditor', href: `${ENTER_FEATURES_PATH}/visual-editor`, internal: false },
                { labelKey: 'dropdown.collaborativeCoding', href: `${ENTER_FEATURES_PATH}/collaborative-coding`, internal: false },
                { labelKey: 'dropdown.aiWebsiteBuilder', href: `${ENTER_FEATURES_PATH}/ai-website-builder`, internal: false },
                { labelKey: 'dropdown.aiAppBuilder', href: `${ENTER_FEATURES_PATH}/ai-app-builder`, internal: false },
            ],
        },
    },
    
    // Solutions - all external links
    { 
        labelKey: 'nav.enterAgents', 
        dropdown: true,
        subMenus: {
            'targetUser': [
                { labelKey: 'dropdown.developers', href: `${ENTER_SOLUTIONS_URL}/ai-for-developers`, internal: false },
                { labelKey: 'dropdown.productManager', href: `${ENTER_SOLUTIONS_URL}/ai-for-product-manager`, internal: false },
                { labelKey: 'dropdown.smallBusinesses', href: `${ENTER_SOLUTIONS_URL}/ai-for-small-businesses`, internal: false },
                { labelKey: 'dropdown.startup', href: `${ENTER_SOLUTIONS_URL}/ai-startup`, internal: false },
                { labelKey: 'dropdown.hr', href: `${ENTER_SOLUTIONS_URL}/ai-agents-for-hr`, internal: false },
                { labelKey: 'dropdown.operation', href: `${ENTER_SOLUTIONS_URL}/ai-for-operations`, internal: false },
                { labelKey: 'dropdown.marketing', href: `${ENTER_SOLUTIONS_URL}/ai-for-marketing`, internal: false },
            ],
            'useCases': [
                { labelKey: 'dropdown.saas', href: `${ENTER_SOLUTIONS_URL}/saas-website-builder`, internal: false },
                { labelKey: 'dropdown.productivity', href: `${ENTER_SOLUTIONS_URL}/ai-for-productivity`, internal: false },
                { labelKey: 'dropdown.onlineShop', href: `${ENTER_SOLUTIONS_URL}/online-shop-builder`, internal: false },
                { labelKey: 'dropdown.finance', href: `${ENTER_SOLUTIONS_URL}/ai-for-finance`, internal: false },
                { labelKey: 'dropdown.landingPages', href: `${ENTER_SOLUTIONS_URL}/ai-page-generator`, internal: false },
                { labelKey: 'dropdown.software', href: `${ENTER_SOLUTIONS_URL}/ai-coding-assistant`, internal: false },
                { labelKey: 'dropdown.health', href: `${ENTER_SOLUTIONS_URL}/ai-for-health`, internal: false },
                { labelKey: 'dropdown.education', href: `${ENTER_SOLUTIONS_URL}/ai-for-education`, internal: false },
            ],
        },
    },
    
    // Resources - all external links
    { 
        labelKey: 'nav.resources', 
        dropdown: true,
        subMenus: {
            'blog': [
                { labelKey: 'dropdown.announcement', href: 'https://enter.converge.ai/blog?category=Announcement', internal: false },
                { labelKey: 'dropdown.changelog', href: 'https://enter.converge.ai/blog?category=Changelog', internal: false },
                { labelKey: 'dropdown.event', href: 'https://enter.converge.ai/blog?category=Event', internal: false },
                { labelKey: 'dropdown.guide', href: 'https://enter.converge.ai/blog?category=Guide', internal: false },
                { labelKey: 'dropdown.insight', href: 'https://enter.converge.ai/blog?category=Insight', internal: false },
                { labelKey: 'dropdown.userStory', href: 'https://enter.converge.ai/blog?category=User%20Story', internal: false },
            ],
            'program': [
                { labelKey: 'dropdown.ambassador', href: 'https://ambassador.enter.pro/', internal: false },
                { labelKey: 'dropdown.school', href: 'https://enter.converge.ai/school', internal: false },
                { labelKey: 'dropdown.affiliate', href: 'https://enter.converge.ai/affiliate', internal: false },
            ],
        },
    },
    
    // Community - internal link to the design kit page.
    { labelKey: 'nav.community', href: '/marketplace/design-kit', internal: true },

    // Pricing - open the matching Enter environment pricing page.
    { labelKey: 'nav.pricing', href: '/pricing', environmentRoute: 'pricing' },
];

type HeaderPrefetchLinkProps = Omit<React.ComponentProps<typeof Link>, 'href' | 'prefetch'> & {
    /** 点击和预取共用的当前语言路径。 */
    href: string;
};

function HeaderPrefetchLink({ href, onMouseEnter, onFocus, ...props }: HeaderPrefetchLinkProps): React.ReactElement {
    const [intentHref, setIntentHref] = useState<string | null>(null);
    return (
        <Link
            {...props}
            href={href}
            prefetch={intentHref === href}
            onMouseEnter={(event) => {
                onMouseEnter?.(event);
                if (!event.defaultPrevented && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
                    setIntentHref(href);
                }
            }}
            onFocus={(event) => {
                onFocus?.(event);
                if (!event.defaultPrevented) setIntentHref(href);
            }}
        />
    );
}

const ExternalLinkIcon = () => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="ml-1"
    >
        <path
            d="M3.5 2.5H9.5V8.5M9.5 2.5L2.5 9.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

interface HeaderMotionLabelProps {
    /** Text rendered in both animation layers. */
    label: string;
    /** Whether the animated two-layer treatment is enabled. */
    enabled: boolean;
}

const HeaderMotionLabel: React.FC<HeaderMotionLabelProps> = ({ label, enabled }) => {
    if (!enabled) return <>{label}</>;

    const characters = Array.from(label);

    return (
        <span className="site-header__motion-label">
            <span className="sr-only">{label}</span>
            {characters.map((character, index) => {
                const visibleCharacter = character === ' ' ? '\u00a0' : character;

                return (
                    <span
                        key={`${character}-${index}`}
                        aria-hidden="true"
                        className="site-header__motion-character"
                        style={{ '--motion-character-index': index } as React.CSSProperties}
                    >
                        <span className="site-header__motion-label-layer site-header__motion-label-layer--outgoing">
                            {visibleCharacter}
                        </span>
                        <span className="site-header__motion-label-layer site-header__motion-label-layer--incoming">
                            {visibleCharacter}
                        </span>
                    </span>
                );
            })}
        </span>
    );
};

export interface HeaderProps {
    /** 通知 Feature 已准备的展示数据。 */
    notificationBanner?: NotificationBannerProps | null;
    /** 通知关闭动作由业务 owner 提供。 */
    onNotificationBannerClose?: (banner?: NotificationBannerCarouselItem) => void;
    /** Optional custom class name. */
    className?: string;
    /** Whether the session-scoped sign-in popup may open automatically. */
    allowSigninPopupAutoOpen?: boolean;
    /** Whether the notification banner may be rendered when banner data exists. */
    showNotificationBanner?: boolean;
    /** Whether known notification banner data should reserve its layout space while hidden. */
    reserveNotificationBannerSpace?: boolean;
    /** Whether the banner uses the full dynamic presentation or the Landing V2 static presentation. */
    notificationBannerMode?: 'dynamic' | 'static';
    /** Whether desktop navigation and action labels use the rolling hover treatment. */
    enableTextMotion?: boolean;
    /** Enables the Landing V2 unified button contract without changing other pages. */
    trackingSurface?: 'landing_v2';
    /** 按动作读取页面会话上下文，不转发组件事件或持有页面状态。 */
    getTrackingContext?: () => Record<string, unknown> | null;
}

export const Header: React.FC<HeaderProps> = ({
    className = '',
    allowSigninPopupAutoOpen = true,
    showNotificationBanner = true,
    reserveNotificationBannerSpace = false,
    notificationBannerMode = 'dynamic',
    enableTextMotion = false,
    trackingSurface,
    getTrackingContext,
    notificationBanner = null,
    onNotificationBannerClose,
}) => {
    const t = useTranslations('header');
    const locale = useLocale();
    const pathname = usePathname();
    const router = useRouter();
    const searchParams = useSearchParams();
    const localizedPathname = stripLocalePrefix(pathname);
    const localizedNavigation = useLocalizedNavigation();
    const { login } = useEnterHomeLogin();
    const { getDistinctId, logger } = useMixpanel();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [returnToUrl, setReturnToUrl] = useState<string | undefined>(undefined);
    const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
    const signinPopupViewedRef = useRef(false);
    const signinPopupContentRef = useRef<HTMLDivElement | null>(null);
    const [isSigninPopupOpen, setIsSigninPopupOpen] = useState(false);
    const [isLoginPending, setIsLoginPending] = useState(false);
    const [isSignupPending, setIsSignupPending] = useState(false);
    const isLandingV2 = trackingSurface === 'landing_v2';
    const prefetchHome = () => router.prefetch(localizedNavigation.href('/'));

    const trackButton = (input: { buttonId: string; screenId: string; actionType: string; destination?: string; contentKey?: string; creationType?: string; triggersLogin?: boolean }) => {
        if (!isLandingV2) return;
        const common = getTrackingContext?.();
        if (!common) return;
        const attempt = input.triggersLogin
            ? createEnterHomeAuthAttempt({
                  entry:
                      input.buttonId === 'header.sign_in'
                          ? 'home_header_signin'
                          : input.buttonId === 'header.sign_up'
                            ? 'home_header_signup'
                            : input.buttonId === 'header.signin_popup.cta'
                              ? 'home_signin_popup'
                              : 'product_auth_gate',
                  loginIntent:
                      input.buttonId === 'header.sign_up' ||
                      input.buttonId === 'header.signin_popup.cta'
                          ? 'signup'
                          : 'login',
                  mixpanelDistinctId: getDistinctId(),
              })
            : null;
        logger('home_landing_button_click', {
            ...common,
            button_id: input.buttonId,
            screen_id: input.screenId,
            action_type: input.actionType,
            ...(input.destination ? { destination: input.destination } : {}),
            ...(input.contentKey ? { content_key: input.contentKey } : {}),
            ...(input.creationType ? { creation_type: input.creationType } : {}),
            ...(input.triggersLogin ? { triggers_login: true } : {}),
            ...(attempt
                ? {
                      entry: attempt.entry,
                      login_intent: attempt.loginIntent,
                      auth_trace_id: attempt.authTraceId,
                  }
                : {}),
        });
    };
    const handleActionCapture = (event: React.SyntheticEvent) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const element = target.closest<HTMLElement>('[data-landing-button-id]');
        if (!element || (event.type === 'click' && element.tagName === 'SELECT')) return;
        const { landingButtonId, landingButtonScreen, landingAction, landingDestination, landingContentKey, landingCreationType, landingTriggersLogin } = element.dataset;
        if (!landingButtonId || !landingButtonScreen || !landingAction) return;
        trackButton({
            buttonId: landingButtonId, screenId: landingButtonScreen, actionType: landingAction,
            destination: landingDestination,
            contentKey: event.type === 'change' && target instanceof HTMLSelectElement ? target.value : landingContentKey,
            creationType: landingCreationType === 'app' || landingCreationType === 'agent' ? landingCreationType : undefined,
            triggersLogin: landingTriggersLogin === 'true',
        });
    };

    useEffect(() => {
        const resetAuthPending = () => {
            setIsLoginPending(false);
            setIsSignupPending(false);
        };
        window.addEventListener('pageshow', resetAuthPending);
        return () => window.removeEventListener('pageshow', resetAuthPending);
    }, []);

    const trackLegacyHeaderClick = (slug: string) => {
        if (!isLandingV2) logger('home_header_item_click', { slug });
    };

    const landingButtonAttrs = (
        buttonId: string,
        actionType: LandingV2ActionType,
        destination?: string,
        creationType?: 'app' | 'agent',
        triggersLogin = false,
    ): Partial<LandingButtonTrackingAttributes> =>
        isLandingV2
            ? {
                  'data-landing-button-id': buttonId,
                  'data-landing-button-screen': 'hero',
                  'data-landing-action': actionType,
                  ...(destination
                      ? {
                            'data-landing-destination': destination,
                            'data-landing-content-key': destination,
                        }
                      : {}),
                  ...(creationType ? { 'data-landing-creation-type': creationType } : {}),
                  ...(triggersLogin ? { 'data-landing-triggers-login': 'true' } : {}),
              }
            : {};

    const handleDropdownMouseEnter = (key: string) => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        setOpenDropdown(key);
    };

    const handleDropdownMouseLeave = (key: string) => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        closeTimerRef.current = setTimeout(() => {
            // Only close if this dropdown is still the active one
            setOpenDropdown(current => current === key ? null : current);
        }, 200);
    };

    const handleDropdownOpenChange = (key: string, open: boolean) => {
        if (open) {
            setOpenDropdown(key);
            return;
        }
        setOpenDropdown((current) => (current === key ? null : current));
    };

    const logSigninPopupView = () => {
        if (signinPopupViewedRef.current) return;
        signinPopupViewedRef.current = true;
        logger('home_header_signin_popup_view', { slug: 'signin' });
    };

    const markSigninPopupDismissed = () => {
        try {
            window.sessionStorage.setItem(SIGNIN_POPUP_DISMISSED_KEY, '1');
        } catch {
            // sessionStorage 不可用时静默降级，不阻断关闭行为
        }
    };

    const closeSigninPopup = (reason: 'close_button' | 'outside') => {
        setIsSigninPopupOpen(false);
        markSigninPopupDismissed();
        logger('home_header_signin_popup_close', { slug: 'signin', reason });
    };

    // Radix Popover 在点击外部 / Esc 时回调 open=false。
    // 自动展开后用户点外部关闭时，等同一次主动关闭并记录 dismissed。
    const handleSigninPopupOpenChange = (open: boolean) => {
        if (open) {
            setIsSigninPopupOpen(true);
            logSigninPopupView();
            return;
        }
        if (isSigninPopupOpen) {
            closeSigninPopup('outside');
        } else {
            setIsSigninPopupOpen(false);
        }
    };

    const hasBanner = !!notificationBanner;
    const shouldRenderBanner = showNotificationBanner && hasBanner;
    const shouldReserveBannerSpace = hasBanner && (showNotificationBanner || reserveNotificationBannerSpace);
    const notificationBannerLayout = getHeaderBannerLayout(notificationBanner);
    const usesDynamicNotificationBanner = notificationBannerMode === 'dynamic';
    const notificationBannerSpacerClassName = usesDynamicNotificationBanner
        ? notificationBannerLayout.spacerClassName
        : 'h-[32px]';

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setOpenMobileDropdown(null);
    }, [pathname]);

    useEffect(() => {
        setReturnToUrl(window.location.href);
    }, []);

    // 需求：获得页面许可后弹窗默认自动出现，不再依赖 hover。
    // 每个浏览器会话仅首次自动弹一次：用 sessionStorage 记 dismissed 标记。
    // 关闭（关闭按钮 / 点外部 / Esc）后本会话内不再自动弹出。
    useEffect(() => {
        // Landing V2 的 Header 会先于开场结束挂载。开场期间保持关闭，
        // 等 introComplete 变为 true 后再沿用原有的会话级自动展开规则。
        if (!allowSigninPopupAutoOpen) return;

        let dismissed = false;
        try {
            dismissed = window.sessionStorage.getItem(SIGNIN_POPUP_DISMISSED_KEY) === '1';
        } catch {
            dismissed = false;
        }
        if (dismissed) return;
        // 等首帧渲染后再展开，确保 Popover anchor（Sign Up 按钮）已经测量到位，
        // 避免首屏定位竞态导致弹窗错位/不可见。
        const raf = window.requestAnimationFrame(() => {
            setIsSigninPopupOpen(true);
            logSigninPopupView();
        });
        return () => window.cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowSigninPopupAutoOpen]);

    const redirectToLogin = (analyticsAttempt?: EnterHomeAuthAttempt) =>
        login({
            appState: {
                returnTo: '/workspace/',
            },
            authorizationParams: {
                prompt: 'login',
            },
            analyticsAttempt,
            authEntry: 'home_header_signin',
            loginIntent: 'login',
        });

    const redirectToSignup = (
        authEntry: 'home_header_signup' | 'home_signin_popup',
        analyticsAttempt?: EnterHomeAuthAttempt,
    ) =>
        login({
            appState: {
                returnTo: '/workspace/',
            },
            authorizationParams: {
                // prompt=login 强制展示认证页，避免已有 Auth0 SSO 会话直接静默登录。
                // screen_hint=signup 让认证页直接打开注册。
                prompt: 'login',
                screen_hint: 'signup',
            },
            analyticsAttempt,
            authEntry,
            loginIntent: 'signup',
        });

    const handleLogin = () => {
        if (isLoginPending || isSignupPending) return;

        const attempt = isLandingV2
            ? undefined
            : createEnterHomeAuthAttempt({
                  entry: 'home_header_signin',
                  loginIntent: 'login',
                  mixpanelDistinctId: getDistinctId(),
              });
        if (!isLandingV2) {
            logger('home_header_item_click', {
                slug: 'signin',
                auth_trace_id: attempt?.authTraceId,
            });
        }
        setIsLoginPending(true);
        void redirectToLogin(attempt).catch(() => setIsLoginPending(false));
    };

    const handleSignup = () => {
        if (isLoginPending || isSignupPending) return;

        const attempt = isLandingV2
            ? undefined
            : createEnterHomeAuthAttempt({
                  entry: 'home_header_signup',
                  loginIntent: 'signup',
                  mixpanelDistinctId: getDistinctId(),
              });
        if (!isLandingV2) {
            logger('home_header_item_click', {
                slug: 'signup',
                auth_trace_id: attempt?.authTraceId,
            });
        }
        setIsSignupPending(true);
        void redirectToSignup('home_header_signup', attempt).catch(() =>
            setIsSignupPending(false),
        );
    };

    const handleSigninPopupCta = () => {
        if (isLoginPending || isSignupPending) return;

        const attempt = isLandingV2
            ? undefined
            : createEnterHomeAuthAttempt({
                  entry: 'home_signin_popup',
                  loginIntent: 'signup',
                  mixpanelDistinctId: getDistinctId(),
              });
        logger(
            'home_header_signin_popup_click',
            {
                slug: 'signup',
                ...(attempt ? { auth_trace_id: attempt.authTraceId } : {}),
            },
            { sendImmediately: true },
        );
        setIsSignupPending(true);
        void redirectToSignup('home_signin_popup', attempt).catch(() =>
            setIsSignupPending(false),
        );
    };

    const handleContactSalesClick = () => {
        // 打点：保持 header item 点击事件命名一致，slug 标记为 contact_sales。
        trackLegacyHeaderClick('contact_sales');
        // 透传当前页 URL 上的埋点 / UTM 参数（utm_source 等）到 contact-sales 页，保证归因链路不断。
        const search = typeof searchParams?.toString === 'function' ? searchParams.toString() : '';
        const target = appendTrackingParams(CONTACT_SALE_PATH, search);
        // 站内跳转：useLocalizedNavigation().push 会用 buildLocalizedPath 自动补齐当前语言前缀，
        // 从而保留当前语言（例如印尼语访问点击后进入印尼语 /contact-sales）。
        localizedNavigation.push(target);
    };

    const isActiveLink = (href: string) => {
        if (href === '/') {
            return localizedPathname === '/';
        }
        return localizedPathname.startsWith(href);
    };

    const isDropdownActive = (item: NavItem) => {
        if (!item.subMenus) return false;
        const allHrefs = Object.values(item.subMenus).flat().map(subItem => subItem.href);
        return allHrefs.some(href => {
            // Only check internal links for active state
            if (!href.startsWith('/')) return false;
            return localizedPathname.startsWith(href);
        });
    };

    const buildPricingHref = (businessTraceId?: string) => {
        return buildEnterHomePricingUrl(ENTER_HOME_PRICING_PATH, undefined, {
            businessTraceId,
            entry: 'header.pricing',
            locale,
            returnTo: typeof window === 'undefined' ? undefined : window.location.href,
        });
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen((isOpen) => {
            if (isOpen) setOpenMobileDropdown(null);
            return !isOpen;
        });
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
        setOpenMobileDropdown(null);
    };

    const toggleMobileDropdown = (dropdownKey: string) => {
        setOpenMobileDropdown((current) => current === dropdownKey ? null : dropdownKey);
    };

    const resolveNavHref = (item: NavItem) => {
        if (item.environmentRoute === 'pricing') {
            return buildEnterHomePricingUrl(ENTER_HOME_PRICING_PATH, undefined, {
                entry: 'header.pricing',
                locale,
                returnTo: returnToUrl,
            });
        }

        return item.href || '/';
    };

    const openPricingUrl = () => {
        const businessTraceId = crypto.randomUUID();
        trackLegacyHeaderClick('pricing');
        const result = openEnterHomePricingWindow(buildPricingHref(businessTraceId));
        const track = buildEnterHomePricingNavigationTrack({
            businessTraceId,
            entry: 'header.pricing',
            result,
        });
        logger(track.eventName, track.props, track.options);
        closeMobileMenu();
    };

    const navigateFromDropdown = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
        // Popover 关闭会同步卸载下拉内容。先命令式导航，再关闭菜单，
        // 避免 Next Link 在卸载过程中只 preventDefault 但丢失导航。
        if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }

        event.preventDefault();
        localizedNavigation.push(href);
        setOpenDropdown(null);
    };

    const renderDesktopNav = () => {
        return NAV_ITEMS.map((item) => {
            if (item.environmentRoute === 'pricing') {
                const href = resolveNavHref(item);

                return (
                    <a
                        key={href}
                        href={href}
                        onClick={(event) => {
                            event.preventDefault();
                            openPricingUrl();
                        }}
                        className={cn("site-header__nav-link flex h-8 items-center justify-center border-b border-transparent text-base font-normal leading-6 text-primary/62 no-underline [font-synthesis:none] [font-family:'Google_Sans_Flex','Google_Sans',var(--font-instrument-sans),var(--font-roboto),sans-serif] transition-[color,border-color] duration-200 ease-[ease] [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary", isActiveLink(item.href || '/pricing') ? 'site-header__nav-link--active text-primary border-primary' : '')}
                        {...landingButtonAttrs('header.nav.pricing', 'navigate', 'pricing')}
                    >
                        <HeaderMotionLabel label={t(item.labelKey)} enabled={enableTextMotion} />
                    </a>
                );
            }

            // External top-level links
            if (!item.internal && item.href) {
                return (
                    <a
                        key={item.href}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn("site-header__nav-link flex h-8 items-center justify-center border-b border-transparent text-base font-normal leading-6 text-primary/62 no-underline [font-synthesis:none] [font-family:'Google_Sans_Flex','Google_Sans',var(--font-instrument-sans),var(--font-roboto),sans-serif] transition-[color,border-color] duration-200 ease-[ease] [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary", item.showExternalIcon ? 'site-header__nav-link--external inline-flex' : '')}
                        {...landingButtonAttrs(`header.nav.${item.labelKey}`, 'navigate', item.labelKey)}
                    >
                        <HeaderMotionLabel label={t(item.labelKey)} enabled={enableTextMotion} />
                        {item.showExternalIcon && <ExternalLinkIcon />}
                    </a>
                );
            }
            
            // Dropdown menus
            if (item.dropdown && item.subMenus) {
                const dropdownKey = item.labelKey;
                const isOpen = openDropdown === dropdownKey;
                const isActive = isDropdownActive(item);
                
                return (
                    <Popover key={dropdownKey} open={isOpen} onOpenChange={(open) => handleDropdownOpenChange(dropdownKey, open)}>
                        <PopoverTrigger asChild>
                            <button
                                className={cn("site-header__nav-link flex h-8 items-center justify-center border-b border-transparent text-base font-normal leading-6 text-primary/62 no-underline [font-synthesis:none] [font-family:'Google_Sans_Flex','Google_Sans',var(--font-instrument-sans),var(--font-roboto),sans-serif] transition-[color,border-color] duration-200 ease-[ease] [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary", isActive ? 'site-header__nav-link--active text-primary border-primary' : '')}
                                onMouseEnter={() => handleDropdownMouseEnter(dropdownKey)}
                                onMouseLeave={() => handleDropdownMouseLeave(dropdownKey)}
                                {...landingButtonAttrs(`header.dropdown.${dropdownKey}`, 'toggle', dropdownKey)}
                            >
                                <HeaderMotionLabel label={t(item.labelKey)} enabled={enableTextMotion} />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            data-landing-tracking-owner="component"
                            className="site-header__popover z-[1100] bg-background text-primary border-[#e5e5e5] w-[500px] p-0 rounded-sm shadow-md"
                            align="start"
                            sideOffset={4}
                            onOpenAutoFocus={(e) => e.preventDefault()}
                            onCloseAutoFocus={(e) => e.preventDefault()}
                            onMouseEnter={() => handleDropdownMouseEnter(dropdownKey)}
                            onMouseLeave={() => handleDropdownMouseLeave(dropdownKey)}
                        >
                            <div className="grid grid-cols-2 gap-0">
                                {Object.entries(item.subMenus).map(([groupKey, subItems], index) => (
                                    <div 
                                        key={groupKey}
                                        className={cn("p-2", index > 0 ? 'border-l border-[#e5e5e5]' : '')}
                                    >
                                        {groupKey === 'features' ? (
                                            <a
                                                href={localizedNavigation.href(ENTER_FEATURES_PATH)}
                                                className="site-header__popover-heading-link [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary block px-2.5 py-2 text-xs text-muted-foreground leading-none font-semibold transition-colors"
                                                onClick={() => setOpenDropdown(null)}
                                                {...landingButtonAttrs(
                                                    'header.dropdown.features_all',
                                                    'navigate',
                                                    'features',
                                                )}
                                            >
                                                {t(`dropdown.${groupKey}`)}
                                            </a>
                                        ) : (
                                            <p className="px-2.5 py-2 text-xs text-muted-foreground leading-none font-semibold">
                                                {t(`dropdown.${groupKey}`)}
                                            </p>
                                        )}
                                        {subItems.map((subItem) => {
                                            // Internal links
                                            if (subItem.internal) {
                                                return (
                                                    <HeaderPrefetchLink
                                                        key={subItem.href}
                                                        href={localizedNavigation.href(subItem.href)}
                                                        onClick={(event) => navigateFromDropdown(event, subItem.href)}
                                                        {...landingButtonAttrs(
                                                            `header.dropdown.${subItem.labelKey}`,
                                                            'navigate',
                                                            subItem.labelKey,
                                                        )}
                                                        className={cn("flex h-8 w-full items-center rounded-md px-2.5 text-sm transition-colors", localizedPathname.startsWith(subItem.href)
                                                                ? 'bg-accent text-primary'
                                                                : 'site-header__popover-link [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/6 text-primary')}
                                                    >
                                                        {t(subItem.labelKey)}
                                                    </HeaderPrefetchLink>
                                                );
                                            }
                                            // 非 Next Link 的整页导航：相对路径仍保留 localizedNavigation 的 locale/env 能力，
                                            // 但不让 Popover 卸载打断 Next Link 的 client navigation。
                                            const isSameOriginPath = subItem.href.startsWith('/');
                                            return (
                                                <a
                                                    key={subItem.href}
                                                    href={isSameOriginPath ? localizedNavigation.href(subItem.href) : subItem.href}
                                                    target={isSameOriginPath ? undefined : '_blank'}
                                                    rel={isSameOriginPath ? undefined : 'noopener noreferrer'}
                                                    className="site-header__popover-link [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/6 flex h-8 w-full items-center rounded-md px-2.5 text-sm transition-colors text-primary"
                                                    onClick={() => setOpenDropdown(null)}
                                                    {...landingButtonAttrs(
                                                        `header.dropdown.${subItem.labelKey}`,
                                                        'navigate',
                                                        subItem.labelKey,
                                                    )}
                                                >
                                                    {t(subItem.labelKey)}
                                                </a>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                );
            }
            
            // Internal top-level links
            return (
                <HeaderPrefetchLink
                    key={item.href}
                    href={localizedNavigation.href(item.href!)}
                    className={cn("site-header__nav-link flex h-8 items-center justify-center border-b border-transparent text-base font-normal leading-6 text-primary/62 no-underline [font-synthesis:none] [font-family:'Google_Sans_Flex','Google_Sans',var(--font-instrument-sans),var(--font-roboto),sans-serif] transition-[color,border-color] duration-200 ease-[ease] [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary", isActiveLink(item.href!) ? 'site-header__nav-link--active text-primary border-primary' : '')}
                    {...landingButtonAttrs(`header.nav.${item.labelKey}`, 'navigate', item.labelKey)}
                >
                    <HeaderMotionLabel label={t(item.labelKey)} enabled={enableTextMotion} />
                </HeaderPrefetchLink>
            );
        });
    };

    const renderMobileNav = () => {
        return NAV_ITEMS.map((item) => {
            if (item.environmentRoute === 'pricing') {
                const href = resolveNavHref(item);

                return (
                    <a
                        key={href}
                        href={href}
                        onClick={(event) => {
                            event.preventDefault();
                            openPricingUrl();
                        }}
                        className={cn("site-header__mobile-link flex min-h-[52px] items-center border-b border-primary/10 px-2 py-3 text-lg font-medium leading-6 text-primary no-underline transition-[color,background-color] duration-200 ease-[ease] [font-family:var(--font-instrument-sans),var(--font-inter),sans-serif] focus-visible:bg-primary/6 focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/6 [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary", isActiveLink(item.href || '/pricing') ? 'site-header__mobile-link--active bg-primary/6' : '')}
                        {...landingButtonAttrs('header.mobile.pricing', 'navigate', 'pricing')}
                    >
                        {t(item.labelKey)}
                    </a>
                );
            }

            // External top-level links
            if (!item.internal && item.href) {
                return (
                    <a
                        key={item.href}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn("site-header__mobile-link flex min-h-[52px] items-center border-b border-primary/10 px-2 py-3 text-lg font-medium leading-6 text-primary no-underline transition-[color,background-color] duration-200 ease-[ease] [font-family:var(--font-instrument-sans),var(--font-inter),sans-serif] focus-visible:bg-primary/6 focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/6 [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary", item.showExternalIcon ? 'site-header__mobile-link--external' : '')}
                        onClick={closeMobileMenu}
                        {...landingButtonAttrs(`header.mobile.${item.labelKey}`, 'navigate', item.labelKey)}
                    >
                        {t(item.labelKey)}
                        {item.showExternalIcon && <ExternalLinkIcon />}
                    </a>
                );
            }
            
            // 移动端保留桌面 mega menu 的信息层级，通过单开手风琴渐进披露内容。
            if (item.dropdown && item.subMenus) {
                const dropdownKey = item.labelKey;
                const isExpanded = openMobileDropdown === dropdownKey;

                return (
                    <section className="site-header__mobile-section border-b border-primary/10" key={dropdownKey}>
                        <button
                            type="button"
                            className={cn("site-header__mobile-group-trigger flex min-h-[52px] w-full cursor-pointer items-center justify-between gap-4 border-0 bg-transparent px-2 py-3 text-left text-lg font-medium leading-6 text-primary transition-[color,background-color] duration-200 ease-[ease] [font-family:var(--font-instrument-sans),var(--font-inter),sans-serif] focus-visible:bg-primary/6 focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/6 [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary", isDropdownActive(item) ? 'site-header__mobile-group-trigger--active bg-primary/6' : '')}
                            aria-expanded={isExpanded}
                            aria-controls={`site-header-mobile-panel-${dropdownKey}`}
                            onClick={() => toggleMobileDropdown(dropdownKey)}
                            {...landingButtonAttrs(`header.mobile_dropdown.${dropdownKey}`, 'toggle', dropdownKey)}
                        >
                            <span>{t(item.labelKey)}</span>
                            <ChevronDown
                                aria-hidden="true"
                                className={cn("site-header__mobile-group-icon flex-none transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none", isExpanded ? 'site-header__mobile-group-icon--open rotate-180' : '')}
                                size={18}
                                strokeWidth={1.75}
                            />
                        </button>

                        <div
                            id={`site-header-mobile-panel-${dropdownKey}`}
                            className={cn("site-header__mobile-panel [&[hidden]]:hidden", isExpanded ? 'site-header__mobile-panel--open' : '')}
                            aria-hidden={!isExpanded}
                            hidden={!isExpanded}
                        >
                            <div className="site-header__mobile-panel-inner grid gap-5 pt-2 pr-2 pb-5 pl-6">
                                {Object.entries(item.subMenus).map(([groupKey, subItems]) => (
                                    <div className="site-header__mobile-subgroup grid gap-1" key={groupKey}>
                                        <p className="site-header__mobile-subgroup-title m-0 px-2 py-1 text-xs font-semibold leading-4 tracking-[0.08em] text-primary/50 uppercase [font-family:var(--font-instrument-sans),var(--font-inter),sans-serif]">
                                            {t(`dropdown.${groupKey}`)}
                                        </p>
                                        <div className="site-header__mobile-subgroup-links grid">
                                            {subItems.map((subItem) => {
                                                if (subItem.internal) {
                                                    return (
                                                        <Link
                                                            key={subItem.href}
                                                            href={localizedNavigation.href(subItem.href)}
                                                            onClick={closeMobileMenu}
                                                            {...landingButtonAttrs(
                                                                `header.mobile_dropdown.${subItem.labelKey}`,
                                                                'navigate',
                                                                subItem.labelKey,
                                                            )}
                                                            className={cn("site-header__mobile-sublink flex min-h-11 items-center px-2 py-2.5 text-base font-normal leading-6 text-primary/72 no-underline transition-[color,background-color] duration-200 ease-[ease] [font-family:var(--font-instrument-sans),var(--font-inter),sans-serif] focus-visible:bg-primary/6 focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/6 [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary", localizedPathname.startsWith(subItem.href)
                                                                    ? 'site-header__mobile-sublink--active text-primary bg-primary/6'
                                                                    : '')}
                                                        >
                                                            {t(subItem.labelKey)}
                                                        </Link>
                                                    );
                                                }

                                                const isSameOriginPath = subItem.href.startsWith('/');
                                                return (
                                                    <a
                                                        key={subItem.href}
                                                        href={isSameOriginPath ? localizedNavigation.href(subItem.href) : subItem.href}
                                                        target={isSameOriginPath ? undefined : '_blank'}
                                                        rel={isSameOriginPath ? undefined : 'noopener noreferrer'}
                                                        className="site-header__mobile-sublink flex min-h-11 items-center px-2 py-2.5 text-base font-normal leading-6 text-primary/72 no-underline transition-[color,background-color] duration-200 ease-[ease] [font-family:var(--font-instrument-sans),var(--font-inter),sans-serif] focus-visible:bg-primary/6 focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/6 [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary"
                                                        onClick={closeMobileMenu}
                                                        {...landingButtonAttrs(
                                                            `header.mobile_dropdown.${subItem.labelKey}`,
                                                            'navigate',
                                                            subItem.labelKey,
                                                        )}
                                                    >
                                                        {t(subItem.labelKey)}
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                );
            }
            
            // Internal top-level links
            return (
                <Link
                    key={item.href}
                    href={localizedNavigation.href(item.href!)}
                    onClick={closeMobileMenu}
                    className={cn("site-header__mobile-link flex min-h-[52px] items-center border-b border-primary/10 px-2 py-3 text-lg font-medium leading-6 text-primary no-underline transition-[color,background-color] duration-200 ease-[ease] [font-family:var(--font-instrument-sans),var(--font-inter),sans-serif] focus-visible:bg-primary/6 focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/6 [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary", isActiveLink(item.href!) ? 'site-header__mobile-link--active bg-primary/6' : '')}
                    {...landingButtonAttrs(`header.mobile.${item.labelKey}`, 'navigate', item.labelKey)}
                >
                    {t(item.labelKey)}
                </Link>
            );
        });
    };

    return (
        <>
            {shouldRenderBanner && (
                <div
                    className="site-header__notification-banner fixed top-0 left-0 right-0 z-[49]"
                    data-testid="enter-home-notification-banner-shell"
                    data-landing-tracking-owner="component"
                >
                    <NotificationBanner
                        id={String(notificationBanner.id)}
                        message={notificationBanner.message}
                        variant={notificationBanner.variant}
                        countdownEndTime={
                            usesDynamicNotificationBanner
                                ? notificationBanner.countdownEndTime
                                : undefined
                        }
                        carouselItems={
                            usesDynamicNotificationBanner
                                ? notificationBanner.carouselItems
                                : undefined
                        }
                        carouselIntervalMs={
                            usesDynamicNotificationBanner
                                ? notificationBanner.carouselIntervalMs
                                : undefined
                        }
                        ctaText={notificationBanner.ctaText}
                        ctaLink={notificationBanner.ctaLink}
                        showCta={notificationBanner.showCta}
                        onCtaClick={(banner) => {
                            if (isLandingV2) {
                                trackButton({
                                    buttonId: 'header.notification.cta',
                                    screenId: 'hero',
                                    actionType: 'navigate',
                                    destination: 'notification',
                                    contentKey: String(banner.id),
                                });
                            }
                            notificationBanner.onCtaClick?.(banner);
                        }}
                        ctaLinkType={notificationBanner.ctaLinkType}
                        showCloseButton={notificationBanner.showCloseButton}
                        onClose={(banner) => {
                            if (isLandingV2) {
                                trackButton({
                                    buttonId: 'header.notification.close',
                                    screenId: 'hero',
                                    actionType: 'toggle',
                                    destination: 'notification',
                                    contentKey: banner ? String(banner.id) : undefined,
                                });
                            }
                            onNotificationBannerClose?.(banner);
                        }}
                    />
                </div>
            )}

            <header
                data-landing-tracking-owner="component"
                onClickCapture={handleActionCapture}
                onChangeCapture={handleActionCapture}
                className={cn(
                    'site-header fixed inset-x-0 top-0 z-[1000] flex h-16 w-full flex-col items-center border-b border-black/6 bg-background/96 px-10 backdrop-blur-[16px] transition-[background-color,backdrop-filter] duration-300 ease-[ease] [@media(480px<width<=768px)]:px-4 [@media(width<=480px)]:px-3 [&_.enter-home-brand-logo]:text-primary',
                    usesDynamicNotificationBanner && isScrolled && 'site-header--scrolled',
                    shouldReserveBannerSpace && 'site-header--with-banner top-8',
                    shouldReserveBannerSpace &&
                        usesDynamicNotificationBanner &&
                        notificationBannerLayout.hasCountdown &&
                        'site-header--with-countdown-banner top-9',
                    localizedPathname.startsWith('/docs') && 'site-header--docs p-0!',
                    className
                )}
            >
                <div className={cn(
                    'site-header__container mx-auto flex h-16 w-full max-w-[1680px] items-center justify-between py-3',
                    localizedPathname.startsWith('/docs') && 'max-w-none! px-[160px]! py-3!'
                )}>
                    <div
                        className="site-header__logo flex h-10 w-[200px] shrink-0 items-center gap-2 [@media(width<=1024px)]:w-auto"
                        onMouseEnter={() => {
                            if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) prefetchHome();
                        }}
                        onFocus={prefetchHome}
                        {...landingButtonAttrs('header.logo', 'navigate', 'home')}
                    >
                        <Logo ariaLabel={t('actions.home')} />
                    </div>

                    <nav className="site-header__nav flex flex-1 flex-row items-center justify-center gap-8 [@media(768px<width<=1024px)]:gap-6 [@media(width<=768px)]:hidden">
                        {renderDesktopNav()}
                    </nav>

                    <div className="site-header__actions flex flex-row items-center gap-3">
                        <button
                            className="site-header__contact-sales-btn flex h-9 cursor-pointer items-center justify-center gap-2 rounded-full border-0 bg-[#f0f0ef] px-4 py-2 text-sm font-medium leading-[22px] text-primary transition-[background-color] duration-200 ease-[ease] [@media(width<=768px)]:px-3 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-[#e5e5e3] [font-family:var(--font-instrument-sans),var(--font-roboto),sans-serif]"
                            onClick={handleContactSalesClick}
                            {...landingButtonAttrs('header.contact_sales', 'navigate', 'contact-sales')}
                        >
                            <HeaderMotionLabel label={t('actions.contactSales')} enabled={enableTextMotion} />
                        </button>

                        <button
                            className="site-header__login-btn flex h-9 cursor-pointer items-center justify-center gap-2 rounded-full border-0 bg-[#f0f0ef] px-4 py-2 text-sm font-medium leading-[22px] text-primary transition-[background-color] duration-200 ease-[ease] disabled:cursor-wait disabled:opacity-60 [@media(width<=768px)]:px-3 [@media(hover:hover)_and_(pointer:fine)]:enabled:hover:bg-[#e5e5e3] [font-family:var(--font-instrument-sans),var(--font-roboto),sans-serif]"
                            onClick={handleLogin}
                            disabled={isLoginPending || isSignupPending}
                            aria-busy={isLoginPending}
                            {...landingButtonAttrs('header.sign_in', 'login', undefined, 'app', true)}
                        >
                            {isLoginPending ? (
                                <span className="site-header__login-progress inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
                                    <Loader2
                                        aria-hidden="true"
                                        className="site-header__login-spinner size-3.5 flex-none"
                                        data-testid="header-login-spinner"
                                    />
                                    {t('actions.signingIn')}
                                </span>
                            ) : (
                                <HeaderMotionLabel label={t('actions.signIn')} enabled={enableTextMotion} />
                            )}
                        </button>

                        {/*
                          受控 Popover：open 完全由 isSigninPopupOpen state 驱动。
                          最右侧 Sign Up 按钮仅作 PopoverAnchor。align=end 让弹窗右边缘与该按钮右边缘对齐，
                          弹窗仍出现在锚点下方。不用 PopoverTrigger，避免点击按钮同时触发/重开 Popover。
                          Sign Up 的 onClick 只调 handleSignup，不触碰 Popover 状态。
                          弹窗仅在页面首次加载时由 useEffect 自动展开（维持 sessionStorage 会话级去重）。
                        */}
                        <Popover open={isSigninPopupOpen} onOpenChange={handleSigninPopupOpenChange}>
                            <PopoverAnchor asChild>
                                <button
                                    className="site-header__signup-btn flex h-9 cursor-pointer items-center justify-center gap-2 rounded-full border-0 bg-primary px-4 py-2 text-sm font-medium leading-[22px] text-white transition-[background-color] duration-200 ease-[ease] disabled:cursor-wait disabled:opacity-60 [@media(width<=768px)]:px-3 [@media(hover:hover)_and_(pointer:fine)]:enabled:hover:bg-[#262626] [font-family:var(--font-instrument-sans),var(--font-roboto),sans-serif]"
                                    onClick={handleSignup}
                                    disabled={isLoginPending || isSignupPending}
                                    aria-busy={isSignupPending}
                                    {...landingButtonAttrs('header.sign_up', 'login', undefined, 'app', true)}
                                >
                                    {isSignupPending ? (
                                        <span className="site-header__signup-progress inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
                                            <Loader2
                                                aria-hidden="true"
                                                className="site-header__signup-spinner size-3.5 flex-none"
                                                data-testid="header-signup-spinner"
                                            />
                                            {t('actions.signUp')}
                                        </span>
                                    ) : (
                                        <HeaderMotionLabel label={t('actions.signUp')} enabled={enableTextMotion} />
                                    )}
                                </button>
                            </PopoverAnchor>
                            <PopoverContent
                                data-landing-tracking-owner="component"
                                className="site-header__popover z-[1100] bg-background text-primary border-[#e5e5e5] w-[320px] p-0 rounded-sm shadow-md overflow-hidden"
                                align="end"
                                sideOffset={8}
                                collisionPadding={12}
                                onOpenAutoFocus={(e) => e.preventDefault()}
                                onCloseAutoFocus={(e) => e.preventDefault()}
                                // 防御性修复：泳道/SSR/hydration 环境下曾出现"点击弹窗内部就关闭"。
                                // Radix 仅应在点击弹窗外部 / Esc 时关闭，这里逐项拦截"非外部"触发的关闭。
                                onPointerDownOutside={(event) => {
                                    // 仅允许真正的外部指针按下触发关闭；落在弹窗内部的一律拦截。
                                    const target = event.target as Node | null;
                                    if (target && signinPopupContentRef.current?.contains(target)) {
                                        event.preventDefault();
                                    }
                                }}
                                onInteractOutside={(event) => {
                                    // 防御：内部交互（如内部聚焦/指针）不应触发关闭。
                                    const target = event.target as Node | null;
                                    if (target && signinPopupContentRef.current?.contains(target)) {
                                        event.preventDefault();
                                    }
                                }}
                                onFocusOutside={(event) => event.preventDefault()}
                            >
                                {/* 额外防御层：阻止内部点击冒泡到可能触发关闭的外层 dismiss 逻辑。 */}
                                <div
                                    ref={signinPopupContentRef}
                                    onClick={(event) => event.stopPropagation()}
                                    onPointerDown={(event) => event.stopPropagation()}
                                >
                                    <button
                                        type="button"
                                        aria-label={t('signinPopup.close')}
                                        onClick={() => closeSigninPopup('close_button')}
                                        className="site-header__signin-popup-close [@media(hover:hover)_and_(pointer:fine)]:hover:bg-black/60 absolute right-2 top-2 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/40 text-white transition-colors"
                                        {...landingButtonAttrs('header.signin_popup.close', 'toggle', 'signin-popup')}
                                    >
                                        <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M3 3L9 9M9 3L3 9"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </button>
                                    <div className="overflow-hidden rounded-t-sm bg-muted">
                                        <img
                                            src="/_enter_home/signin-popup/free-credits.png"
                                            alt={t('signinPopup.freeCreditsAlt')}
                                            width={666}
                                            height={300}
                                            loading="lazy"
                                            decoding="async"
                                            className="block w-full h-auto"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2 p-4">
                                        <p className="text-sm font-semibold leading-snug text-primary">
                                            {t('signinPopup.title')}
                                        </p>
                                        <p className="text-xs leading-relaxed text-muted-foreground">
                                            {t('signinPopup.description')}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleSigninPopupCta}
                                            disabled={isLoginPending || isSignupPending}
                                            aria-busy={isSignupPending}
                                            className="site-header__signin-popup-cta disabled:cursor-wait disabled:opacity-60 [@media(hover:hover)_and_(pointer:fine)]:enabled:hover:opacity-90 mt-1 inline-flex h-9 w-full items-center justify-center rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground transition-opacity"
                                            {...landingButtonAttrs(
                                                'header.signin_popup.cta',
                                                'login',
                                                undefined,
                                                'app',
                                                true,
                                            )}
                                        >
                                            {isSignupPending ? (
                                                <span className="site-header__signup-progress inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
                                                    <Loader2
                                                        aria-hidden="true"
                                                        className="site-header__signup-spinner size-3.5 flex-none"
                                                    />
                                                    {t('signinPopup.cta')}
                                                </span>
                                            ) : (
                                                t('signinPopup.cta')
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>

                        <button
                            className="site-header__mobile-toggle hidden size-10 cursor-pointer border-0 bg-transparent p-2 [@media(width<=768px)]:flex items-center justify-center"
                            onClick={toggleMobileMenu}
                            aria-label={t('actions.toggleMenu')}
                            aria-expanded={isMobileMenuOpen}
                            {...landingButtonAttrs('header.mobile_menu', 'toggle', 'mobile-menu')}
                        >
                            <span className={cn("site-header__hamburger flex h-[18px] w-6 flex-col justify-between [&>span]:block [&>span]:h-0.5 [&>span]:w-full [&>span]:rounded-[1px] [&>span]:bg-primary [&>span]:transition-all [&>span]:duration-300 [&>span]:ease-[ease]", isMobileMenuOpen ? 'site-header__hamburger--open [&>span:nth-child(1)]:[transform:translateY(8px)_rotate(45deg)] [&>span:nth-child(2)]:opacity-0 [&>span:nth-child(3)]:[transform:translateY(-8px)_rotate(-45deg)]' : '')}>
                                <span></span>
                                <span></span>
                                <span></span>
                            </span>
                        </button>
                    </div>
                </div>

                <div className={cn(
                    'site-header__mobile-nav absolute inset-x-0 top-16 hidden max-h-[calc(100dvh-64px)] -translate-y-full flex-col overflow-y-auto overscroll-contain border-b border-black/8 bg-background/98 px-4 pt-2 pb-6 opacity-0 backdrop-blur-[16px] transition-[transform,opacity] duration-300 ease-[ease] pointer-events-none [-webkit-overflow-scrolling:touch] [@media(width<=768px)]:flex',
                    shouldReserveBannerSpace && 'max-h-[calc(100dvh-96px)]',
                    isMobileMenuOpen && 'site-header__mobile-nav--open translate-y-0 opacity-100 pointer-events-auto'
                )}>
                    {renderMobileNav()}
                </div>
            </header>

            {shouldReserveBannerSpace && (
                <div
                    aria-hidden="true"
                    className={`site-header__notification-banner-spacer ${notificationBannerSpacerClassName}`}
                    data-testid="enter-home-notification-banner-spacer"
                />
            )}
        </>
    );
};

export default Header;
